import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NoTranslationBadge, SourceBadge } from '@/components/provenance-badge';
import { Icon, StatusPill } from './uploadUi';
import { statsOf } from './extractionStats';
import type { ExtractedPassage, ExtractionResult, SectionKind } from './uploadTypes';

/**
 * What the file produced — shown *before* anything is queued.
 *
 * The point of this card is that extraction is a result, not a progress step.
 * An editor decides whether to spend the AI budget on this khutbah by looking
 * at what came out of the file, so the counts, the section breakdown and the
 * first passages are all here before any generation is offered.
 *
 * Two rules the numbers have to keep:
 *
 * - **Null is not zero.** Plain text carries no font information, so Qur'an
 *   and hadith quotations cannot be identified at all — the card shows an em
 *   dash and says *cannot identify*. Rendering 0 would tell an editor the
 *   khutbah contains no Qur'an, which is a claim nothing has established.
 * - **Warnings sit beside the result, never instead of it.** A file that
 *   extracted with caveats still extracted.
 */

const SECTION_LABELS: Record<SectionKind, string> = {
  opening: 'Opening',
  'first-khutbah': 'First khutbah',
  'second-khutbah': 'Second khutbah',
  supplication: 'Supplication',
};

/** The design system's section ramp, in the order sections actually occur. */
const SECTION_ORDER: SectionKind[] = [
  'opening',
  'first-khutbah',
  'second-khutbah',
  'supplication',
];

const SECTION_COLOURS: Record<SectionKind, string> = {
  opening: 'var(--teal-300)',
  'first-khutbah': 'var(--primary)',
  'second-khutbah': 'var(--teal-800)',
  supplication: 'var(--gold)',
};

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-border bg-muted px-3 py-2.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <b className="text-base font-semibold leading-6 tabular-nums">{value}</b>
      <span className="text-xs text-muted-foreground">{sub}</span>
    </div>
  );
}

function SectionBreakdown({
  sections,
}: {
  sections: readonly { kind: SectionKind; count: number }[];
}) {
  const total = sections.reduce((sum, section) => sum + section.count, 0);
  if (total === 0) {
    return null;
  }

  const ordered = SECTION_ORDER.flatMap((kind) => {
    const found = sections.find((section) => section.kind === kind);
    return found ? [found] : [];
  });

  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Sections
      </p>
      <div
        className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-progress-track"
        aria-hidden
      >
        {ordered.map((section) => (
          <div
            key={section.kind}
            style={{
              width: `${(section.count / total) * 100}%`,
              background: SECTION_COLOURS[section.kind],
            }}
          />
        ))}
      </div>
      {/* The bar is decoration; this list is the actual information, which is
          why the bar is aria-hidden and every swatch is paired with a word. */}
      <ul className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {ordered.map((section) => (
          <li key={section.kind} className="flex items-start gap-2">
            <i
              aria-hidden
              className="mt-1 block size-2.5 shrink-0 rounded-[2px]"
              style={{ background: SECTION_COLOURS[section.kind] }}
            />
            <span className="flex flex-col">
              <span className="text-[13px] font-medium">
                {SECTION_LABELS[section.kind]}
              </span>
              <span className="text-xs text-muted-foreground">
                {section.count} passage{section.count === 1 ? '' : 's'}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The first passages, as extracted.
 *
 * RTL, tashkeel intact, never bold, never editable. A Qur'an passage is
 * framed in ﴿ ﴾ — and when the format could not identify quotations at all,
 * the absence of a frame is explained rather than left to be read as "no
 * Qur'an here".
 */
function Preview({
  passages,
  quotationsIdentifiable,
}: {
  passages: readonly ExtractedPassage[];
  quotationsIdentifiable: boolean;
}) {
  const shown = passages.slice(0, 3);
  if (shown.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Preview · first passages
      </p>
      <div className="reader flex flex-col gap-3 rounded-lg border border-border p-4">
        {shown.map((passage) => {
          const isQuran = passage.type === 'quran';
          const body = (
            <p
              lang="ar"
              dir="rtl"
              className="script-ar text-[18px] font-normal leading-[34px] text-reading-foreground"
            >
              {isQuran ? (
                <>
                  <span className="text-citation-foreground" aria-hidden>
                    ﴿
                  </span>{' '}
                  {passage.ar}{' '}
                  <span className="text-citation-foreground" aria-hidden>
                    ﴾
                  </span>
                </>
              ) : (
                passage.ar
              )}
            </p>
          );

          if (!isQuran && passage.type !== 'hadith') {
            return (
              <div key={passage.n} className="flex flex-col gap-1">
                {body}
              </div>
            );
          }

          return (
            <div
              key={passage.n}
              className="rounded-md border border-quote-arabic-border bg-quote-arabic-bg px-3.5 py-2.5"
            >
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5 rounded-sm bg-citation-bg px-2 py-1 text-[11px] font-semibold text-citation-foreground">
                  <Icon name="sparkle" size={13} />
                  {isQuran ? 'Qur’an' : 'Hadith'}
                  <span className="font-normal italic opacity-80">
                    {/* A reference is only ever shown once matching produced
                        one, and says so until an editor confirms it. Naming a
                        surah nothing has verified is forbidden outright. */}
                    {passage.ref
                      ? `· ${passage.ref.label}${
                          passage.ref.confirmed ? '' : ' · detected by matching'
                        }`
                      : '· reference not yet confirmed'}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Icon name="info" size={13} />
                  Not editable
                </span>
              </div>
              {body}
            </div>
          );
        })}

        {!quotationsIdentifiable ? (
          <p className="text-xs text-muted-foreground" style={{ direction: 'ltr' }}>
            Nothing above is framed as Qur’an: plain text carries no font cue, so no
            verse was identified while reading the file. Mark them in the editor.
          </p>
        ) : null}

        <p className="text-xs text-muted-foreground" style={{ direction: 'ltr' }}>
          Arabic is shown as extracted: RTL, tashkeel intact, never bold.
        </p>
      </div>
    </div>
  );
}

export function ExtractionSummary({
  result,
  onOpen,
  generationAvailable = false,
  onGenerate,
}: {
  result: ExtractionResult;
  onOpen: () => void;
  /** False until the generation pipeline exists; see the footer comment. */
  generationAvailable?: boolean;
  onGenerate?: () => void;
}) {
  const stats = statsOf(result);
  const quotationsIdentifiable = result.quran !== null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>
              <h2>What the file produced</h2>
            </CardTitle>
            <CardDescription>
              Extracted now, before anything is queued. Check it before you generate.
            </CardDescription>
          </div>
          <StatusPill tone="done">Extracted</StatusPill>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {/* Both badges, always. Source verification never implies translation
            review, and showing one without the other invites that reading. */}
        <div className="flex flex-wrap gap-1.5">
          {/* The server sends a publisher key exactly when the source is a
              publisher, and null otherwise — asserted by UploadControllerIT's
              provenance pair, because a user upload badged as publisher-sourced
              is the false claim brief §5 forbids. The badge stays one of the
              canonical six; which ministry it was is named in Details. */}
          <SourceBadge institutionKey={result.provenance.publisher ?? ''} />
          <NoTranslationBadge />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((stat) => (
            <Stat key={stat.label} {...stat} />
          ))}
        </div>

        <SectionBreakdown sections={result.sections} />

        {result.warnings.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Extraction warnings
            </p>
            {result.warnings.map((warning) => (
              <div
                key={warning.code + warning.text}
                className="flex items-start gap-2.5 rounded-md bg-warning-bg p-2.5"
              >
                <Icon name="alert" size={16} className="mt-0.5 text-warning" />
                <span className="text-[13px] leading-[18px] text-pretty">
                  {warning.text}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <Preview
          passages={result.passages}
          quotationsIdentifiable={quotationsIdentifiable}
        />

        {result.blocked ? (
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-border p-3.5"
          >
            <Icon name="clock" size={18} className="mt-0.5 text-muted-foreground" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <p className="font-semibold">The monthly AI budget is reached</p>
              <p className="text-[13px] leading-[18px] text-pretty text-muted-foreground">
                The khutbah is saved and will generate when the budget resets on{' '}
                {result.blocked.resetsOn} or is raised. Nothing is lost; you do not
                need to upload it again.
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusPill tone="done">Khutbah saved</StatusPill>
                <StatusPill tone="queued">Queued until budget resets</StatusPill>
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>

      <CardFooter className="flex-wrap justify-between gap-3">
        <span className="max-w-[380px] text-xs text-pretty text-muted-foreground">
          The khutbah is already saved — the upload created it. Generating translations
          spends the monthly AI budget, and they arrive in the review queue as{' '}
          <i>AI translation · unreviewed</i>.
        </span>
        <div className="flex gap-2">
          <Button type="button" size="lg" variant="outline" onClick={onOpen}>
            Open the khutbah
          </Button>
          {/*
            Disabled, with the reason said out loud, rather than a button that
            posts to an endpoint that does not exist. The translation pipeline
            is still outstanding for Phase 1 (it needs the provider keys), and
            the project's established answer for a destination that is not
            built is a disabled control that explains itself — a dead action
            reads as a broken product.
          */}
          <Button
            type="button"
            size="lg"
            disabled={!generationAvailable}
            onClick={onGenerate}
            title={
              generationAvailable
                ? undefined
                : 'Not built yet — AI translation needs the provider credentials.'
            }
            className="h-11"
          >
            <Icon name="sparkle" size={16} />
            <span>Generate translation and learning content</span>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
